import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash, FloppyDisk, Question, Books } from '@phosphor-icons/react';

/**
 * Custom-question editor for a single job. Employer-only.
 * Short & long text only — keeps it simple and job-family-aware without AI generation.
 * Includes a template browser so employers can import curated question packs.
 */
export const CustomQuestionsEditor = ({ jobId, initialQuestions = [], roleFamilyId = null, onSaved = null }) => {
  const [questions, setQuestions] = useState(initialQuestions);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [templates, setTemplates] = useState(null);  // null = loading; [] = empty; list = loaded
  const [applying, setApplying] = useState(null);

  useEffect(() => {
    setQuestions(initialQuestions || []);
    setDirty(false);
  }, [initialQuestions]);

  const openBrowse = async () => {
    setBrowseOpen(true);
    setTemplates(null);
    try {
      const url = roleFamilyId
        ? `/question-templates?role_family_id=${roleFamilyId}`
        : '/question-templates';
      const r = await api.get(url);
      setTemplates(r.data || []);
    } catch {
      setTemplates([]);
      toast.error('Could not load templates');
    }
  };

  const applyTemplate = async (tplId, mode) => {
    setApplying(tplId);
    try {
      await api.post(`/question-templates/${tplId}/apply/${jobId}?mode=${mode}`);
      toast.success(mode === 'replace' ? 'Template applied (replaced)' : 'Template added');
      setBrowseOpen(false);
      // Parent should refresh the job; if not provided, reload the page
      if (onSaved) onSaved();
      else window.location.reload();
    } catch {
      toast.error('Could not apply template');
    } finally {
      setApplying(null);
    }
  };

  const addQuestion = () => {
    setQuestions((qs) => [...qs, {
      id: crypto.randomUUID(),
      prompt: '',
      type: 'short',
      required: true,
      choices: [],
    }]);
    setDirty(true);
  };

  const updateAt = (idx, patch) => {
    setQuestions((qs) => qs.map((q, i) => i === idx ? { ...q, ...patch } : q));
    setDirty(true);
  };

  const removeAt = (idx) => {
    setQuestions((qs) => qs.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const clean = questions
        .map((q) => ({ ...q, prompt: (q.prompt || '').trim() }))
        .filter((q) => q.prompt);
      await api.put(`/jobs/${jobId}/custom-questions`, { custom_questions: clean });
      setQuestions(clean);
      setDirty(false);
      toast.success('Custom questions saved');
    } catch {
      toast.error('Could not save questions');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-border bg-white p-5 mb-8" data-testid="custom-questions-editor">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Question size={18} weight="duotone" className="text-primary" />
          <div>
            <div className="label-eyebrow text-muted-foreground">JOB-SPECIFIC QUESTIONS</div>
            <div className="text-sm">
              Ask candidates a few targeted questions. Role-family assessments stay the same —
              these are <em>on top</em>, specific to this role.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="rounded-none" onClick={openBrowse} data-testid="browse-templates">
            <Books size={14} className="mr-1" /> Templates
          </Button>
          <Button size="sm" variant="outline" className="rounded-none" onClick={addQuestion} data-testid="add-question">
            <Plus size={14} className="mr-1" /> Add
          </Button>
          <Button size="sm" disabled={!dirty || saving} onClick={save} className="rounded-none" data-testid="save-questions">
            <FloppyDisk size={14} className="mr-1" /> {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {questions.length === 0 ? (
        <div className="mono text-xs text-muted-foreground">
          No custom questions yet. Click <strong>Add</strong> to create one — e.g. “Tell us about a time you handled a stuck shipment.”
        </div>
      ) : (
        <ul className="space-y-3">
          {questions.map((q, idx) => (
            <li key={q.id} className="border border-border p-3 grid md:grid-cols-12 gap-2" data-testid={`question-row-${idx}`}>
              <div className="md:col-span-1 mono text-xs text-muted-foreground pt-2">{String(idx + 1).padStart(2, '0')}</div>
              <div className="md:col-span-9 space-y-2">
                <Textarea
                  value={q.prompt}
                  onChange={(e) => updateAt(idx, { prompt: e.target.value })}
                  placeholder="What do you want to ask?"
                  className="rounded-none"
                  rows={2}
                  data-testid={`question-prompt-${idx}`}
                />
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-1 text-xs">
                    <input
                      type="radio"
                      checked={q.type === 'short'}
                      onChange={() => updateAt(idx, { type: 'short' })}
                      data-testid={`question-type-short-${idx}`}
                    />
                    Short answer
                  </label>
                  <label className="flex items-center gap-1 text-xs">
                    <input
                      type="radio"
                      checked={q.type === 'long'}
                      onChange={() => updateAt(idx, { type: 'long' })}
                      data-testid={`question-type-long-${idx}`}
                    />
                    Long answer
                  </label>
                  <label className="flex items-center gap-1 text-xs ml-auto">
                    <input
                      type="checkbox"
                      checked={q.required}
                      onChange={(e) => updateAt(idx, { required: e.target.checked })}
                      data-testid={`question-required-${idx}`}
                    />
                    Required
                  </label>
                </div>
              </div>
              <div className="md:col-span-2 flex md:justify-end items-start">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeAt(idx)}
                  className="rounded-none text-destructive border-destructive/30 hover:bg-destructive/10"
                  data-testid={`question-remove-${idx}`}
                >
                  <Trash size={14} />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Template browser dialog */}
      <Dialog open={browseOpen} onOpenChange={setBrowseOpen}>
        <DialogContent className="max-w-2xl rounded-none max-h-[85vh] overflow-y-auto" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl tracking-tighter">
              Curated question packs
            </DialogTitle>
            <DialogDescription>
              Import a proven pack on top of your existing questions. You can still edit after.
            </DialogDescription>
          </DialogHeader>
          {templates === null ? (
            <div className="mono text-sm text-muted-foreground p-4" data-testid="templates-loading">Loading templates…</div>
          ) : templates.length === 0 ? (
            <div className="mono text-sm text-muted-foreground p-4">No templates available yet.</div>
          ) : (
            <ul className="space-y-3">
              {templates.map((t) => (
                <li key={t.id} className="border border-border p-4" data-testid={`template-${t.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-display font-black text-lg">{t.title}</div>
                      <p className="text-sm text-muted-foreground mt-1">{t.description}</p>
                      <div className="mono text-xs text-muted-foreground mt-2">
                        {t.questions?.length || 0} questions · {t.role_family_id ? 'role-specific' : 'universal'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button
                        size="sm"
                        className="rounded-none"
                        disabled={applying === t.id}
                        onClick={() => applyTemplate(t.id, 'append')}
                        data-testid={`template-append-${t.id}`}
                      >
                        {applying === t.id ? 'Applying…' : 'Add pack'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none"
                        onClick={() => applyTemplate(t.id, 'replace')}
                        data-testid={`template-replace-${t.id}`}
                      >
                        Replace all
                      </Button>
                    </div>
                  </div>
                  <ul className="mt-3 space-y-1">
                    {(t.questions || []).slice(0, 3).map((q) => (
                      <li key={q.id} className="text-sm text-muted-foreground">· {q.prompt}</li>
                    ))}
                    {(t.questions || []).length > 3 && (
                      <li className="mono text-xs text-muted-foreground">+ {t.questions.length - 3} more</li>
                    )}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomQuestionsEditor;
